import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    Download,
    ChevronLeft,
    ChevronRight,
    Forward,
    Sparkles,
    Copy,
    Check,
    ZoomIn,
    ZoomOut,
    RotateCw,
    File,
    FileText,
    FileSpreadsheet,
    FileType,
    FileArchive,
    Search,
    Users,
    MessageCircle
} from 'lucide-react';
import AiLoadingOverlay from './AiLoadingOverlay';
import './FileViewer.css';

const getFileIcon = (mimeType, fileName, size = 48) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (mimeType?.includes('pdf') || ext === 'pdf')
        return <FileText size={size} className="fv-icon pdf" />;
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext))
        return <FileSpreadsheet size={size} className="fv-icon excel" />;
    if (mimeType?.includes('word') || mimeType?.includes('document') || ['doc', 'docx'].includes(ext))
        return <FileText size={size} className="fv-icon word" />;
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint') || ['ppt', 'pptx'].includes(ext))
        return <FileType size={size} className="fv-icon ppt" />;
    if (mimeType?.includes('zip') || mimeType?.includes('rar') || ['zip', 'rar', '7z'].includes(ext))
        return <FileArchive size={size} className="fv-icon archive" />;
    return <File size={size} className="fv-icon" />;
};

const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const OCR_PHASES = [
    'Escaneando documento...',
    'Extrayendo texto...',
    'Analizando con IA...'
];

const FileViewer = ({
    file,
    senderName,
    timestamp,
    baseUrl,
    allFiles = [],      // [{file: {...}, senderName, timestamp}, ...]
    currentIndex = 0,
    onClose,
    onForward,
    conversations = []
}) => {
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [activeIndex, setActiveIndex] = useState(currentIndex);
    const [showForwardModal, setShowForwardModal] = useState(false);
    const [forwardSearch, setForwardSearch] = useState('');
    const [ocrLoading, setOcrLoading] = useState(false);
    const [ocrResult, setOcrResult] = useState(null);
    const [ocrError, setOcrError] = useState('');
    const imageRef = useRef(null);
    const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

    // Extract the actual file object from the wrapper
    const activeEntry = allFiles.length > 0 ? allFiles[activeIndex] : null;
    const activeFile = activeEntry ? activeEntry.file : file;
    const activeSender = activeEntry ? activeEntry.senderName : senderName;
    const activeTimestamp = activeEntry ? activeEntry.timestamp : timestamp;

    const isImage = activeFile?.mime_type?.startsWith('image/');
    const isPdf = activeFile?.mime_type?.includes('pdf') || activeFile?.original_name?.toLowerCase().endsWith('.pdf');
    const token = localStorage.getItem('token');
    const fileUrl = `${baseUrl}${activeFile?.download_url}?token=${token}`;
    const canExtractText = isImage || isPdf;

    const hasNav = allFiles.length > 1;

    useEffect(() => {
        setZoom(1);
        setRotation(0);
        setPan({ x: 0, y: 0 });
        setOcrResult(null);
        setOcrError('');
    }, [activeIndex]);

    // Drag-to-pan handlers (only when zoomed)
    const handlePointerDown = useCallback((e) => {
        if (zoom <= 1) return;
        e.preventDefault();
        setIsDragging(true);
        dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
        e.target.setPointerCapture(e.pointerId);
    }, [zoom, pan]);

    const handlePointerMove = useCallback((e) => {
        if (!isDragging) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPan({ x: dragStart.current.panX + dx, y: dragStart.current.panY + dy });
    }, [isDragging]);

    const handlePointerUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Reset pan when zoom returns to 1
    const handleZoomChange = useCallback((fn) => {
        setZoom(prev => {
            const next = fn(prev);
            if (next <= 1) setPan({ x: 0, y: 0 });
            return next;
        });
    }, []);

    // Scroll-wheel zoom on the image (non-passive to allow preventDefault)
    useEffect(() => {
        const img = imageRef.current;
        if (!img || ocrResult) return;
        const onWheel = (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.25 : 0.25;
            handleZoomChange(z => Math.min(Math.max(z + delta, 0.25), 4));
        };
        img.addEventListener('wheel', onWheel, { passive: false });
        return () => img.removeEventListener('wheel', onWheel);
    }, [handleZoomChange, ocrResult, activeIndex]);

    // Double-click to reset zoom/pan
    const handleDoubleClick = useCallback(() => {
        if (zoom !== 1) {
            setZoom(1);
            setPan({ x: 0, y: 0 });
        } else {
            setZoom(2);
        }
    }, [zoom]);

    const navigatePrev = useCallback(() => {
        setActiveIndex(i => i > 0 ? i - 1 : allFiles.length - 1);
    }, [allFiles.length]);

    const navigateNext = useCallback(() => {
        setActiveIndex(i => i < allFiles.length - 1 ? i + 1 : 0);
    }, [allFiles.length]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && !ocrResult) onClose();
            if (e.key === 'ArrowLeft' && hasNav) navigatePrev();
            if (e.key === 'ArrowRight' && hasNav) navigateNext();
            if (e.key === '+' || e.key === '=') handleZoomChange(z => Math.min(z + 0.25, 4));
            if (e.key === '-') handleZoomChange(z => Math.max(z - 0.25, 0.25));
            if (e.key === 'r') setRotation(r => r + 90);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasNav, onClose, navigatePrev, navigateNext, ocrResult, handleZoomChange]);

    const handleDownload = () => {
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = activeFile?.original_name || 'archivo';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleForward = (conversationId) => {
        if (onForward && activeFile) {
            onForward(activeFile, conversationId);
            setShowForwardModal(false);
        }
    };

    const handleExtractText = async () => {
        if (!activeFile?.uuid || !token) return;
        setOcrLoading(true);
        setOcrError('');
        try {
            const response = await fetch(`${baseUrl}/api/chat/file/${activeFile.uuid}/ocr`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'No se pudo extraer texto');
            }
            setOcrResult(data);
        } catch (error) {
            setOcrError(error.message || 'Error al procesar OCR');
        } finally {
            setOcrLoading(false);
        }
    };

    const [copiedField, setCopiedField] = useState(null);

    const copyToClipboard = async (text, fieldKey) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldKey || '__text__');
            setTimeout(() => setCopiedField(null), 1500);
        } catch {
            // Ignore clipboard errors in unsupported browsers
        }
    };

    const filteredConversations = conversations.filter(c =>
        !forwardSearch.trim() ||
        c.name?.toLowerCase().includes(forwardSearch.toLowerCase())
    );

    return ReactDOM.createPortal(
        <AnimatePresence>
            <motion.div
                className="file-viewer-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={ocrResult ? undefined : onClose}
            >
                <motion.div
                    className="file-viewer-container"
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="fv-header">
                        <div className="fv-header-info">
                            <span className="fv-filename">{activeFile?.original_name}</span>
                            <span className="fv-meta">
                                {activeSender && <span>{activeSender}</span>}
                                {activeFile?.size_bytes && <span>{formatFileSize(activeFile.size_bytes)}</span>}
                                {activeTimestamp && <span>{activeTimestamp}</span>}
                            </span>
                        </div>
                        <div className="fv-header-actions">
                            {isImage && (
                                <>
                                    <button onClick={() => handleZoomChange(z => Math.min(z + 0.25, 4))} title="Acercar">
                                        <ZoomIn size={18} />
                                    </button>
                                    <button onClick={() => handleZoomChange(z => Math.max(z - 0.25, 0.25))} title="Alejar">
                                        <ZoomOut size={18} />
                                    </button>
                                    <button onClick={() => setRotation(r => r + 90)} title="Rotar">
                                        <RotateCw size={18} />
                                    </button>
                                    <div className="fv-divider" />
                                </>
                            )}
                            {onForward && (
                                <button className="fv-forward-btn" onClick={() => setShowForwardModal(true)}>
                                    <Forward size={16} />
                                    Reenviar
                                </button>
                            )}
                            {canExtractText && (
                                <button
                                    className="fv-ocr-btn"
                                    onClick={handleExtractText}
                                    disabled={ocrLoading}
                                    title="Extraer texto del documento"
                                >
                                    <Sparkles size={16} />
                                    {ocrLoading ? 'Extrayendo...' : 'Extraer texto'}
                                </button>
                            )}
                            <button onClick={handleDownload} title="Descargar">
                                <Download size={18} />
                            </button>
                            <div className="fv-divider" />
                            <button onClick={onClose} title="Cerrar">
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Content — switches to split layout when OCR results are shown */}
                    <div className={`fv-content ${ocrResult ? 'fv-content-split' : ''}`}>
                        {!ocrResult && !ocrLoading && hasNav && (
                            <button className="fv-nav fv-nav-prev" onClick={navigatePrev}>
                                <ChevronLeft size={24} />
                            </button>
                        )}

                        <div className={`fv-main ${ocrResult ? 'fv-main-thumb' : ''} ${ocrLoading ? 'fv-main-blur' : ''}`}>
                            {isImage ? (
                                <img
                                    ref={imageRef}
                                    src={fileUrl}
                                    alt={activeFile?.original_name}
                                    className={`fv-image ${ocrResult ? 'fv-image-thumb' : ''}`}
                                    style={!ocrResult ? {
                                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                                        transition: isDragging ? 'none' : 'transform 0.2s ease',
                                        cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                                    } : undefined}
                                    draggable={false}
                                    onPointerDown={!ocrResult ? handlePointerDown : undefined}
                                    onPointerMove={!ocrResult ? handlePointerMove : undefined}
                                    onPointerUp={!ocrResult ? handlePointerUp : undefined}
                                    onDoubleClick={!ocrResult ? handleDoubleClick : undefined}
                                />
                            ) : isPdf ? (
                                !ocrResult ? (
                                    <iframe
                                        src={`${fileUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                                        title={activeFile?.original_name || 'PDF'}
                                        className="fv-pdf-viewer"
                                    />
                                ) : (
                                    <div className="fv-file-display fv-file-display-mini">
                                        {getFileIcon(activeFile?.mime_type, activeFile?.original_name, 40)}
                                        <span className="fv-mini-name">{activeFile?.original_name}</span>
                                    </div>
                                )
                            ) : (
                                <div className="fv-file-display">
                                    {getFileIcon(activeFile?.mime_type, activeFile?.original_name, 56)}
                                    <h3>{activeFile?.original_name}</h3>
                                    <p>{formatFileSize(activeFile?.size_bytes)}</p>
                                    <button className="fv-download-btn" onClick={handleDownload}>
                                        <Download size={18} />
                                        Descargar archivo
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Loading overlay — AI neural pulse animation */}
                        {ocrLoading && <AiLoadingOverlay phases={OCR_PHASES} />}

                        {ocrResult && (
                            <div className="fv-ocr-panel">
                                <div className="fv-ocr-header">
                                    <div className="fv-ocr-header-left">
                                        {ocrResult.tipo_documento && (
                                            <span className="fv-ocr-doc-type">{ocrResult.tipo_documento}</span>
                                        )}
                                        <span>Analizado con IA</span>
                                    </div>
                                </div>

                                <div className="fv-ocr-content">
                                    {(() => {
                                        const validFields = Object.entries(ocrResult.fields || {})
                                            .filter(([, value]) => value !== null && value !== '' && value !== undefined);
                                        const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                        return validFields.length > 0 && (
                                            <div className="fv-ocr-fields">
                                                {validFields.map(([key, value]) => (
                                                    <div key={key} className={`fv-ocr-field ${String(value).length > 20 ? 'fv-ocr-field-wide' : ''}`}>
                                                        <div className="fv-ocr-field-info">
                                                            <span>{formatLabel(key)}</span>
                                                            <strong>{String(value)}</strong>
                                                        </div>
                                                        <button
                                                            className="fv-ocr-field-copy"
                                                            onClick={() => copyToClipboard(String(value), key)}
                                                            title="Copiar"
                                                        >
                                                            {copiedField === key ? <Check size={13} /> : <Copy size={13} />}
                                                        </button>
                                                        {copiedField === key && (
                                                            <span className="fv-ocr-copied-toast">Copiado</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                    {ocrResult.warnings?.length > 0 && (
                                        <div className="fv-ocr-warnings">
                                            {ocrResult.warnings.join(' ')}
                                        </div>
                                    )}
                                    <div className="fv-ocr-text-box">
                                        <div className="fv-ocr-text-box-header">
                                            <span>Texto extraído</span>
                                            <button
                                                className="fv-ocr-text-copy-btn"
                                                onClick={() => copyToClipboard(ocrResult.text, '__text__')}
                                            >
                                                {copiedField === '__text__' ? (
                                                    <><Check size={12} /> Copiado</>
                                                ) : (
                                                    <><Copy size={12} /> Copiar</>
                                                )}
                                            </button>
                                        </div>
                                        <pre className="fv-ocr-text">{ocrResult.text || 'Sin texto detectado.'}</pre>
                                    </div>
                                </div>
                            </div>
                        )}

                        {ocrError && !ocrResult && (
                            <div className="fv-ocr-error-overlay">
                                <div className="fv-ocr-error-card">
                                    <X size={16} className="fv-ocr-error-close" onClick={() => setOcrError('')} />
                                    <span>{ocrError}</span>
                                </div>
                            </div>
                        )}

                        {!ocrResult && !ocrLoading && hasNav && (
                            <button className="fv-nav fv-nav-next" onClick={navigateNext}>
                                <ChevronRight size={24} />
                            </button>
                        )}
                    </div>

                    {/* Thumbnail strip */}
                    {allFiles.length > 1 && (
                        <div className="fv-thumbnails">
                            {allFiles.map((entry, idx) => {
                                const f = entry.file || entry;
                                const isImg = f?.mime_type?.startsWith('image/');
                                return (
                                    <button
                                        key={idx}
                                        className={`fv-thumb ${idx === activeIndex ? 'active' : ''}`}
                                        onClick={() => setActiveIndex(idx)}
                                    >
                                        {isImg ? (
                                            <img
                                                src={`${baseUrl}${f.download_url}?token=${token}`}
                                                alt=""
                                            />
                                        ) : (
                                            getFileIcon(f?.mime_type, f?.original_name, 18)
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </motion.div>

                {/* Forward Modal */}
                <AnimatePresence>
                    {showForwardModal && (
                        <motion.div
                            className="fv-forward-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={(e) => { e.stopPropagation(); setShowForwardModal(false); }}
                        >
                            <motion.div
                                className="fv-forward-modal"
                                initial={{ scale: 0.95, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.95, y: 20 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="fv-forward-header">
                                    <h3><Forward size={18} /> Reenviar a...</h3>
                                    <button onClick={() => setShowForwardModal(false)}><X size={18} /></button>
                                </div>
                                <div className="fv-forward-search">
                                    <Search size={16} />
                                    <input
                                        type="text"
                                        placeholder="Buscar conversación..."
                                        value={forwardSearch}
                                        onChange={(e) => setForwardSearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="fv-forward-list">
                                    {filteredConversations.map(conv => (
                                        <div
                                            key={conv.id}
                                            className="fv-forward-item"
                                            onClick={() => handleForward(conv.id)}
                                        >
                                            <div className="fv-forward-avatar">
                                                {conv.is_group ? <Users size={18} /> : <MessageCircle size={18} />}
                                            </div>
                                            <span className="fv-forward-name">{conv.name}</span>
                                        </div>
                                    ))}
                                    {filteredConversations.length === 0 && (
                                        <div className="fv-forward-empty">No se encontraron conversaciones</div>
                                    )}
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
};

export default FileViewer;
